import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { userApi, type SavedSearch } from '../../services/userApi';
import { savedSearchToAlertDraft, type AlertDraft } from '../../services/searchToAlert';
import { AlertFormDialog } from './AlertFormDialog';

function describeFilters(s: SavedSearch): string {
  if (!s.filters) return s.query ?? 'No filters';
  const parts: string[] = [];
  if (s.query) parts.push(`q=${s.query}`);
  for (const [key, val] of Object.entries(s.filters)) {
    if (val === undefined || val === null || val === '') continue;
    parts.push(`${key}=${Array.isArray(val) ? val.join(',') : String(val)}`);
  }
  return parts.length === 0 ? 'No filters' : parts.join(' · ');
}

function toSearchUrl(s: SavedSearch): string {
  const params = new URLSearchParams();
  if (s.query) params.set('q', s.query);
  if (s.filters) {
    for (const [key, val] of Object.entries(s.filters)) {
      if (val === undefined || val === null || val === '') continue;
      if (Array.isArray(val)) {
        for (const item of val) params.append(key, String(item));
      } else {
        params.set(key, String(val));
      }
    }
  }
  return `/search${params.toString() ? `?${params.toString()}` : ''}`;
}

/**
 * Saved searches page. Each row offers Run (navigates to /search with
 * the stored filters), Convert to alert (Phase 4 stub — see TODO at
 * the bottom of this file), and Delete with a confirm dialog.
 */
export function SavedSearchesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['user', 'saved-searches'],
    queryFn: () => userApi.listSavedSearches(),
  });

  const [confirmDelete, setConfirmDelete] = useState<SavedSearch | null>(null);
  // "Convert to alert": holds the pre-filled draft + drives the alert dialog.
  // A stable reference while open so AlertFormDialog re-seeds only on change.
  const [convertDraft, setConvertDraft] = useState<AlertDraft | null>(null);
  const [toast, setToast] = useState<string>('');

  const deleteMutation = useMutation({
    mutationFn: (id: string) => userApi.deleteSavedSearch(id),
    onSuccess: () => {
      setConfirmDelete(null);
      return queryClient.invalidateQueries({ queryKey: ['user', 'saved-searches'] });
    },
  });

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Saved searches
      </Typography>

      {query.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading saved searches" />
        </Box>
      ) : query.isError ? (
        <Alert severity="error" role="alert">
          Failed to load saved searches: {(query.error as Error).message}
        </Alert>
      ) : (query.data?.savedSearches.length ?? 0) === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">No saved searches yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Save a search from the results page to revisit it later.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="Saved searches">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Filters</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data!.savedSearches.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{describeFilters(s)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(toSearchUrl(s))}
                        aria-label={`Run saved search ${s.name}`}
                      >
                        Run
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setConvertDraft(savedSearchToAlertDraft(s))}
                        aria-label={`Convert saved search ${s.name} to an alert`}
                      >
                        Convert to alert
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setConfirmDelete(s)}
                        aria-label={`Delete saved search ${s.name}`}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="confirm-delete-search-title"
      >
        <DialogTitle id="confirm-delete-search-title">Delete this saved search?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{confirmDelete?.name}” will be removed from your saved searches.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Convert to alert": opens the alert form pre-filled from the saved
          search's filters. A created alert lives independently under
          /dashboard/alerts (we invalidate that query on save). */}
      <AlertFormDialog
        open={Boolean(convertDraft)}
        initialDraft={convertDraft}
        onClose={() => setConvertDraft(null)}
        onSaved={() => {
          setToast('Alert created from your saved search. Find it under Alerts.');
          return queryClient.invalidateQueries({ queryKey: ['user', 'alerts'] });
        }}
      />

      {/* Polite live region announces the conversion result (§16.3). */}
      <Box
        role="status"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {toast}
      </Box>
    </Box>
  );
}

export default SavedSearchesPage;
